import { convexQuery } from "@convex-dev/react-query";
import { daysOfTheWeek } from "@plasma/data/time";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  Edit3,
  PlusCircle,
  Settings,
  ShieldAlert,
  TagIcon,
  Trash2,
  XCircle,
} from "lucide-react";

import {
  type DiscountAvailabilityTime,
  type DiscountItem,
  locationDiscountsGetAllByLocationId,
} from "~/api-refs";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { toConvexTableId } from "~/lib/convex-table-id";

export const Route = createFileRoute("/_authenticated/locations/$id/discounts")(
  {
    component: LocationDiscounts,
  }
);

function LocationDiscounts() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canManageDiscounts = hasPermission("manageDiscounts");

  const { data } = useQuery(
    convexQuery(locationDiscountsGetAllByLocationId, {
      locationId: toConvexTableId("locations", id),
    })
  );

  // Loading state
  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>No Employee Profile</AlertTitle>
        <AlertDescription>
          Your account is not linked to an employee profile. Contact your
          organization admin to set up your employee record with the appropriate
          permissions.
        </AlertDescription>
      </Alert>
    );
  }

  // No permission to manage discounts
  if (!canManageDiscounts) {
    return (
      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to manage discounts. Contact your manager to
          request access.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="flex justify-between py-5" id="discounts">
        <div className="space-y-1">
          <h2 className="subtitle-sm text-foreground">Discounts</h2>
          <p className="body-sm text-muted-foreground">
            Setup discount options that can be applied in POS.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          {/* V4.1: Route-based settings modal for URL context tracking */}
          {data?.settings && (
            <Button
              className="flex items-center space-x-1.5"
              onClick={() =>
                navigate({ to: `/locations/${id}/discounts/settings` })
              }
              variant="outline"
            >
              <Settings className="size-4" />
              <p>Settings</p>
            </Button>
          )}
          {/* V4.1: Route-based create for URL context tracking */}
          <Button
            className="flex items-center space-x-1.5"
            onClick={() => navigate({ to: `/locations/${id}/discounts/new` })}
          >
            <PlusCircle className="size-4" />
            <p>Create Discount</p>
          </Button>
        </div>
      </div>
      <div className="space-y-2 overflow-x-auto">
        <div
          className="border-border bg-background grid w-full min-w-4xl grid-cols-[2fr_1fr_2fr_100px_120px_80px] gap-0 rounded-xl border p-3"
          id="discounts-table"
        >
          {/* header */}
          <div className="bg-muted rounded-l-2xl">
            <p className="body-sm subtitle-sm text-foreground px-4 py-3">
              Name
            </p>
          </div>
          <div className="bg-muted">
            <p className="body-sm subtitle-sm text-foreground px-4 py-3">
              Amount
            </p>
          </div>
          <div className="bg-muted">
            <p className="body-sm subtitle-sm text-foreground px-4 py-3">
              Available Times
            </p>
          </div>
          <div className="bg-muted">
            <p className="body-sm subtitle-sm text-foreground px-4 py-3">
              Alcohol
            </p>
          </div>
          <div className="bg-muted">
            <p className="body-sm subtitle-sm text-foreground px-4 py-3">
              Restriction
            </p>
          </div>
          <div className="bg-muted rounded-r-2xl" />
          {(!data?.discounts || data.discounts.length === 0) && (
            <div className="col-span-full py-12 text-center">
              <Empty className="border-0">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <TagIcon />
                  </EmptyMedia>
                  <EmptyTitle>No discounts yet</EmptyTitle>
                  <EmptyDescription>
                    Create discounts that employees can apply to orders in POS.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          )}
          {data?.discounts?.map((posDevice: DiscountItem) => (
            <div
              className="hover:bg-muted/40 col-span-full grid grid-cols-subgrid rounded-lg transition-colors"
              key={posDevice._id}
            >
              <div className="flex items-center">
                <p className="body-sm text-foreground px-4 py-3">
                  {posDevice.name}
                </p>
              </div>
              <div className="flex items-center">
                <p className="body-sm text-foreground px-4 py-3">
                  {posDevice.precision === "amount" ? "$ " : ""}
                  {posDevice.value}
                  {posDevice.precision === "percentage" ? " %" : ""}
                </p>
              </div>
              <div className="body-sm text-foreground flex items-center px-4 py-3 whitespace-pre-wrap">
                {posDevice.matchBusinessHours
                  ? "Business hours"
                  : formatAvailabilityTimes(posDevice.availableTimes)}
              </div>
              <div className="flex items-center justify-center px-4">
                {posDevice.applyToAlcohol ? (
                  <CheckCircle2 className="text-success size-4" />
                ) : (
                  <XCircle className="text-destructive size-4" />
                )}
              </div>
              <div className="flex items-center justify-center px-4">
                {posDevice.employeeRestriction ? (
                  <CheckCircle2 className="text-success size-4" />
                ) : (
                  <XCircle className="text-destructive size-4" />
                )}
              </div>
              <div className="flex items-center justify-center space-x-2 px-4 py-3.5">
                <button
                  className="bg-muted rounded-full p-2"
                  onClick={() =>
                    navigate({
                      to: `/locations/${id}/discounts/${posDevice._id}/edit`,
                    })
                  }
                  type="button"
                >
                  <Edit3 className="size-5" />
                </button>
                {/* V4.1: Route-based delete for URL context tracking */}
                <button
                  className="bg-destructive/10 rounded-full p-2"
                  onClick={() =>
                    navigate({
                      to: `/locations/${id}/discounts/${posDevice._id}/delete`,
                    })
                  }
                  type="button"
                >
                  <Trash2 className="text-destructive size-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Outlet for child routes (e.g., /$discountId/edit modal) */}
      <Outlet />
    </>
  );
}

const formatAvailabilityTimes = (
  availabilityTimes: DiscountAvailabilityTime[]
) => {
  const availability: [(typeof daysOfTheWeek)[number], string | null][] =
    daysOfTheWeek.map((day) => {
      const dayAvailability = availabilityTimes.filter(
        (availabilityTime: DiscountAvailabilityTime) =>
          availabilityTime.dayOfTheWeek === day
      );
      if (!dayAvailability.length) {
        return [day, null];
      }
      return [
        day,
        dayAvailability
          .toSorted(
            (a: DiscountAvailabilityTime, b: DiscountAvailabilityTime) =>
              a.startTime.localeCompare(b.startTime)
          )
          .map(
            (availabilityTime: DiscountAvailabilityTime) =>
              `${timeConvert24hrTo12hr(availabilityTime.startTime)} - ${timeConvert24hrTo12hr(availabilityTime.endTime)}`
          )
          .join(", "),
      ];
    });
  const returnArray: [string[], string | null][] = [];
  for (const [day, times] of availability) {
    const lastElement = returnArray.at(-1);
    if (lastElement && lastElement[1] === times) {
      lastElement[0].push(day);
    } else {
      returnArray.push([[day], times]);
    }
  }
  return returnArray
    .filter((d) => d[1])
    .map(([days, time]) => {
      const timeStr = time ?? "";
      if (days.length > 2) {
        return `${days[0]?.slice(0, 3)}-${days.at(-1)?.slice(0, 3)}: ${timeStr}`;
      }
      if (days.length === 2) {
        return `${days[0]?.slice(0, 3)}, ${days[1]?.slice(0, 3)}: ${timeStr}`;
      }
      return `${days[0]?.slice(0, 3)}: ${timeStr}`;
    })
    .join("\n");
};
